import { Box, Divider, Grid2 as Grid, Stack } from "@mui/material";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { DotLegalDialog, DotLegalHeader, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useCreateEditFrameworkControlDialog } from "./CreateEditFrameworkControlDialog.hooks.ts";
import { FrameworkControlTableRow } from "../../frameworkOverview/FrameworkOverview.types.ts";
import { FrameworkControlSaveModel } from "./CreateEditFrameworkControlDialog.types.ts";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import TagsMultiSelect from "../../../common/components/tagsMultiSelect/TagsMultiSelect.tsx";
import { TagRelationType } from "../../../common/components/tagsMultiSelect/TagsMultiSelect.types.ts";

export interface CreateEditFrameworkControlDialogProps {
    onCloseDialog: () => void;
    onSaved: (id: string) => void;
    frameworkId: string;
    selectedControl?: {
        id: string | undefined;
        name: string | undefined;
        description: string | undefined;
        categoryId: string | undefined;
        customId: string | undefined;
        coordinatorId: string | undefined;
        tagIds: Array<string>;
        implementationGroup: string | undefined;
    };
}

function CreateEditFrameworkControlDialog(props: CreateEditFrameworkControlDialogProps) {
    const { translateString } = useTranslation();
    const {
        isEdit,
        modelUpdater,
        saveModel,
        coordinatorOptions,
        coordinatorOptionsIsLoading,
        createControl,
        categoryOptions,
        categoryOptionsIsLoading,
        validation,
        isSaving,
    } = useCreateEditFrameworkControlDialog(props);

    const { permissions } = useUserContext();

    return (
        <DotLegalDialog
            header={isEdit ? translateString("editControl") : translateString("addControlToFramework")}
            buttonOkText={isEdit ? translateString("save") : translateString("create")}
            open
            onDialogClose={props.onCloseDialog}
            onOkClick={() => createControl()}
            size="lg"
            inProgress={isSaving}
        >
            <Box sx={{ marginLeft: 3, marginRight: 3 }}>
                <Grid container columnSpacing={8}>
                    <Grid size={{ sm: 6 }}>
                        <Stack spacing={2.5}>
                            <DotLegalTextField
                                label={translateString("nameOfControl")}
                                errorText={validation.getErrorsForDisplay("name")}
                                value={saveModel.name ?? ""}
                                onChange={modelUpdater.onNameChange}
                                debounce={false}
                                toolTipText={translateString("declarationControlNameTooltip")}
                            />

                            <DotLegalSelect
                                disableClearable
                                selectedItem={saveModel.categoryId}
                                errorText={validation.getErrorsForDisplay("categoryId")}
                                onChange={modelUpdater.onCategoryChange}
                                label={translateString("category")}
                                noOptionsLabel={translateString("noOptions")}
                                toolTipText={translateString("declarationControlCategoryTooltip")}
                                options={categoryOptions}
                                isLoading={categoryOptionsIsLoading}
                            />

                            <DotLegalSelect
                                disableClearable
                                options={coordinatorOptions}
                                isLoading={coordinatorOptionsIsLoading}
                                selectedItem={saveModel.coordinatorId}
                                onChange={modelUpdater.onCoordinatorChange}
                                label={translateString("controlCoordinator")}
                                noOptionsLabel={translateString("noOptions")}
                                toolTipText={translateString("frameworkControlCoordinatorTooltip")}
                                errorText={validation.getErrorsForDisplay("coordinatorId")}
                            />

                            {permissions.canAccessTags && (
                                <TagsMultiSelect
                                    selectedTags={saveModel.tagIds}
                                    onChange={modelUpdater.onTagsChange}
                                    isLoading={false}
                                    entityType={TagRelationType.FrameworkControl}
                                    variant="outlined"
                                />
                            )}
                        </Stack>
                    </Grid>
                    <Grid size={{ sm: 6 }}>
                        <Stack spacing={2.5}>
                            <DotLegalTextField
                                label={translateString("id")}
                                value={saveModel.customId ?? ""}
                                debounce={false}
                                onChange={modelUpdater.onCustomIdChange}
                                toolTipText={translateString("declarationControlIdTooltip")}
                            />

                            <DotLegalTextField
                                label={translateString("implementationGroup")}
                                value={saveModel.implementationGroup ?? ""}
                                debounce={false}
                                onChange={modelUpdater.onImplementationGroupChange}
                            />

                            <DotLegalTextField
                                label={translateString("description")}
                                value={saveModel.description ?? ""}
                                debounce={false}
                                toolTipText={translateString("declarationControlDescriptionTooltip")}
                                multiline
                                rows={3}
                                onChange={modelUpdater.onDescriptionChange}
                            ></DotLegalTextField>
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
        </DotLegalDialog>
    );
}

export default CreateEditFrameworkControlDialog;
